import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Manage Positions modal — v2 (glassmorphism).
 * Uses a hand-rolled backdrop rather than shadcn Dialog for a tighter visual match.
 */
export const ManagePositionsModal = ({ open, onClose, onChanged }) => {
    const [positions, setPositions] = useState([]);
    const [name, setName] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/positions");
            setPositions(data);
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    useEffect(() => {
        if (open) {
            setError("");
            setName("");
            load();
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const add = async (e) => {
        e?.preventDefault?.();
        const n = name.trim();
        if (!n) return;
        setBusy(true);
        setError("");
        try {
            await api.post("/positions", { name: n });
            setName("");
            await load();
            onChanged?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        setBusy(true);
        setError("");
        try {
            await api.delete(`/positions/${id}`);
            setPositions((prev) => prev.filter((p) => p.id !== id));
            onChanged?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15, 23, 42, 0.35)", backdropFilter: "blur(4px)" }}
            data-testid="positions-modal-backdrop"
            onClick={onClose}
        >
            <div
                className="glass-card w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
                data-testid="positions-modal"
                style={{ padding: 0 }}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--divider)]">
                    <div>
                        <div className="text-[15px] font-semibold text-slate-900">
                            Manage positions
                        </div>
                        <div className="text-[12px] text-slate-500">
                            Shared across your team.
                        </div>
                    </div>
                    <button
                        className="btn btn-secondary btn-sm btn-icon"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="positions-modal-close"
                    >
                        <X size={14} strokeWidth={2} />
                    </button>
                </div>

                <div className="p-5">
                    {positions.length === 0 && (
                        <div className="text-[13px] text-slate-500 py-2">
                            No positions yet.
                        </div>
                    )}
                    <ul
                        className="flex flex-col rounded-[10px] overflow-hidden bg-white/40"
                        data-testid="positions-list"
                    >
                        {positions.map((p, i) => (
                            <li
                                key={p.id}
                                className={`flex items-center justify-between px-3 py-2 ${
                                    i > 0 ? "border-t border-[var(--divider)]" : ""
                                }`}
                                data-testid={`position-row-${p.id}`}
                            >
                                <span className="chip chip-neutral">{p.name}</span>
                                <button
                                    className="btn btn-danger btn-sm btn-icon"
                                    onClick={() => remove(p.id)}
                                    disabled={busy}
                                    aria-label={`Remove ${p.name}`}
                                    data-testid={`position-delete-${p.id}`}
                                >
                                    <X size={13} strokeWidth={2} />
                                </button>
                            </li>
                        ))}
                    </ul>

                    <form
                        onSubmit={add}
                        className="mt-4 flex gap-2"
                        data-testid="positions-add-form"
                    >
                        <input
                            className="input flex-1"
                            placeholder="Add a position…"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="positions-add-input"
                        />
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={busy || !name.trim()}
                            data-testid="positions-add-button"
                        >
                            <Plus size={15} strokeWidth={2.5} />
                            Add
                        </button>
                    </form>

                    {error && (
                        <div
                            className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2 mt-4"
                            data-testid="positions-error"
                        >
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManagePositionsModal;
